/**
 * The file upload engine of one open project (docs/specs/cloud/storage-uploads.md §5). The project database queues
 * the files its rows point to (teslyn-db migration 0015, behind {@link UploadQueue}); this engine reads each file,
 * checks it with the domain's upload rules, hashes it, uploads it to Teslyn Cloud Storage as the signed-in user
 * (behind {@link FileStore}) and records the outcome. Nothing here needs the network: a failed upload stays queued
 * with a time to try again.
 *
 * Run after a sync push and pull: a file is offered only once its row has reached the cloud, because the storage
 * policies check the row before they accept its file.
 */
import { fileBaseName, uploadCheck, uploadTarget, UPLOAD_KINDS, type UploadKind } from '@teslyn/domain';
import type { LocalStatus } from './engine.js';

export type { UploadKind } from '@teslyn/domain';
import { NIL_TENANT } from './tables.js';

/** A file waiting to upload (teslyn-db `UploadJob`). */
export interface UploadJob {
  readonly kind: UploadKind;
  readonly record_id: string;
  readonly record_table:
    | 'report'
    | 'fuel_ledger_entry'
    | 'project_photo'
    | 'document_scan'
    | 'safety_brief'
    | 'height_breach'
    | 'project';
  readonly project_id: string;
  /** As the row holds it: absolute (report PDFs) or relative to the project folder (dockets, photos). */
  readonly local_path: string;
  readonly rev: number;
  readonly attempts: number;
  readonly uploaded_sha256: string | null;
  readonly object_path: string | null;
}

interface AckKey {
  readonly kind: UploadKind;
  readonly record_id: string;
  readonly rev: number;
}

/** The outcome of one upload (teslyn-db `UploadAck`). */
export type UploadAck =
  | (AckKey & {
      readonly outcome: 'done';
      readonly sha256: string;
      readonly size_bytes: number;
      readonly object_path: string;
      readonly code: string | null;
    })
  | (AckKey & {
      readonly outcome: 'retry';
      readonly code: string;
      readonly message: string;
      readonly retry_at: string;
    })
  | (AckKey & { readonly outcome: 'refused'; readonly code: string; readonly message: string })
  | (AckKey & { readonly outcome: 'missing' });

export interface UploadInfo {
  readonly waiting: number;
  readonly failed: number;
  readonly uploaded: number;
}

/** A file with a reason (teslyn-db `UploadProblem`). */
export interface UploadProblem {
  readonly kind: UploadKind;
  readonly record_id: string;
  readonly record_table: string;
  readonly local_path: string;
  readonly state: 'pending' | 'refused' | 'missing';
  readonly code: string;
  readonly message: string;
  readonly attempts: number;
  readonly next_attempt_at: string | null;
}

/** The project database's upload queue (Tauri commands upload_* in Field; teslyn-db src/uploads.rs). */
export interface UploadQueue {
  status(): Promise<LocalStatus>;
  uploadPending(limit: number, now: string): Promise<UploadJob[]>;
  uploadAck(acks: readonly UploadAck[]): Promise<void>;
  /** The file's bytes, or null when it is not where the row says; a FileStoreError `too_large` when it is too big. */
  readFile(job: UploadJob): Promise<Uint8Array | null>;
}

/** What Teslyn Cloud holds for a record (`report_publication` or `project_file`). */
export interface CloudFile {
  readonly sha256: string | null;
  readonly source: 'field' | 'portal';
  readonly objectPath: string;
  readonly sizeBytes: number;
}

/** The cloud record written after an object is stored. */
export interface FileRecord {
  readonly kind: UploadKind;
  readonly tenantId: string;
  readonly projectId: string;
  readonly recordId: string;
  readonly bucket: string;
  readonly objectPath: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly sha256: string;
}

export type FileStoreErrorCode = 'offline' | 'server' | 'denied' | 'too_large' | 'bad_type' | 'exists';

/** A Storage or database answer the engine acts on. Any other error (signed out, a bug) stops the run. */
export class FileStoreError extends Error {
  constructor(
    readonly code: FileStoreErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'FileStoreError';
  }
}

/** Teslyn Cloud Storage and the file records, as the signed-in user. */
export interface FileStore {
  current(kind: UploadKind, recordId: string): Promise<CloudFile | null>;
  /** Store an object; with `overwrite` false an existing object is an `exists` error. */
  put(
    bucket: string,
    objectPath: string,
    bytes: Uint8Array,
    contentType: string,
    overwrite: boolean,
  ): Promise<void>;
  record(r: FileRecord): Promise<void>;
  remove(bucket: string, objectPath: string): Promise<void>;
}

export interface FileUploadReport {
  /** `needs_link`: the project was set up offline; `retry_later`: the network failed, files stay queued. */
  readonly state: 'done' | 'needs_link' | 'retry_later';
  readonly uploaded: number;
  /** Already in the cloud with the same content. */
  readonly unchanged: number;
  /** A PDF staff attached in the portal was left as it is. */
  readonly kept_portal: number;
  readonly refused: number;
  readonly missing: number;
  readonly retried: number;
}

export const RETRY_BASE_MS = 60_000;
export const RETRY_MAX_MS = 60 * 60_000;
/** A file the storage policies refuse this many times is refused (its row may never reach the cloud). */
export const MAX_DENIED_ATTEMPTS = 8;
const BATCH = 10;
const MAX_ROUNDS = 20;

/** When to try again after `attempts` earlier failed attempts: 1, 2, 4 … minutes, at most an hour. */
export function retryAt(now: Date, attempts: number): string {
  const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.max(0, attempts));
  return new Date(now.getTime() + delay).toISOString();
}

/** SHA-256 of the bytes in lower-case hex (Web Crypto: Node and WebView2). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

const DENIED_MESSAGE =
  'Teslyn Cloud refused the file. Your role may not upload it, or its record is not in Teslyn Cloud.';

type Outcome = { ack: UploadAck; counted: keyof Omit<FileUploadReport, 'state'>; stop: boolean };

export class FileUploadEngine {
  constructor(
    private readonly queue: UploadQueue,
    private readonly store: FileStore,
    private readonly clock: () => Date = () => new Date(),
  ) {}

  /** Upload every file that is due, in batches, and record the outcomes. */
  async run(): Promise<FileUploadReport> {
    const report = { uploaded: 0, unchanged: 0, kept_portal: 0, refused: 0, missing: 0, retried: 0 };
    const status = await this.queue.status();
    const tenantId = status.tenant_id && status.tenant_id !== NIL_TENANT ? status.tenant_id : null;
    if (!tenantId) return { state: 'needs_link', ...report };
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const jobs = await this.queue.uploadPending(BATCH, this.clock().toISOString());
      if (jobs.length === 0) return { state: 'done', ...report };
      for (const job of jobs) {
        const o = await this.one(job, tenantId);
        await this.queue.uploadAck([o.ack]);
        report[o.counted] += 1;
        if (o.stop) return { state: 'retry_later', ...report };
      }
    }
    return { state: 'retry_later', ...report };
  }

  private async one(job: UploadJob, tenantId: string): Promise<Outcome> {
    const key = { kind: job.kind, record_id: job.record_id, rev: job.rev };
    let read: Uint8Array | null;
    try {
      read = await this.queue.readFile(job);
    } catch (e) {
      // the laptop refuses to hand over a file larger than any bucket takes
      if (!(e instanceof FileStoreError)) throw e;
      return this.failed(job, key, e);
    }
    if (!read) return { ack: { ...key, outcome: 'missing' }, counted: 'missing', stop: false };
    const bytes = read;
    const check = uploadCheck(job.kind, job.local_path, bytes.byteLength);
    if (!check.ok) {
      return {
        ack: { ...key, outcome: 'refused', code: check.code, message: check.message },
        counted: 'refused',
        stop: false,
      };
    }
    const sha256 = await sha256Hex(bytes);
    const done = (objectPath: string, code: string | null = null): UploadAck => ({
      ...key,
      outcome: 'done',
      sha256,
      size_bytes: bytes.byteLength,
      object_path: objectPath,
      code,
    });
    if (job.uploaded_sha256 === sha256 && job.object_path) {
      return { ack: done(job.object_path, 'unchanged'), counted: 'unchanged', stop: false };
    }
    const target = uploadTarget(job.kind, {
      tenantId,
      projectId: job.project_id,
      recordId: job.record_id,
      ext: check.ext,
      sha256,
    });
    try {
      const current = await this.store.current(job.kind, job.record_id);
      if (job.kind === 'report_pdf' && current?.source === 'portal') {
        return { ack: done(current.objectPath, 'attached_in_portal'), counted: 'kept_portal', stop: false };
      }
      if (current && current.sha256 === sha256 && current.objectPath === target.objectPath) {
        return { ack: done(target.objectPath, 'unchanged'), counted: 'unchanged', stop: false };
      }
      // report PDFs keep their report's name; dockets, briefs' PDFs, breach charts and breach reports are named by
      // content, so an existing object is this file; a photo's files are named by the photo and may be written again
      // until recorded (an upload cut off before)
      const overwrite =
        job.kind !== 'fuel_docket' &&
        job.kind !== 'brief_pdf' &&
        job.kind !== 'breach_chart' &&
        job.kind !== 'breach_report';
      try {
        await this.store.put(target.bucket, target.objectPath, bytes, check.contentType, overwrite);
      } catch (e) {
        if (!(e instanceof FileStoreError && e.code === 'exists' && !overwrite)) throw e;
      }
      await this.store.record({
        kind: job.kind,
        tenantId,
        projectId: job.project_id,
        recordId: job.record_id,
        bucket: target.bucket,
        objectPath: target.objectPath,
        fileName: fileBaseName(job.local_path),
        contentType: check.contentType,
        sizeBytes: bytes.byteLength,
        sha256,
      });
      if (current && current.objectPath !== target.objectPath && job.kind === 'fuel_docket') {
        // the record names the new object now; the old one is no longer anyone's file
        await this.store.remove(UPLOAD_KINDS.fuel_docket.bucket, current.objectPath).catch(() => undefined);
      }
      return { ack: done(target.objectPath), counted: 'uploaded', stop: false };
    } catch (e) {
      if (!(e instanceof FileStoreError)) throw e;
      return this.failed(job, key, e);
    }
  }

  private failed(job: UploadJob, key: AckKey, e: FileStoreError): Outcome {
    switch (e.code) {
      case 'too_large':
      case 'bad_type':
        return {
          ack: { ...key, outcome: 'refused', code: e.code, message: e.message },
          counted: 'refused',
          stop: false,
        };
      case 'denied':
        if (job.attempts + 1 >= MAX_DENIED_ATTEMPTS) {
          return {
            ack: { ...key, outcome: 'refused', code: 'denied', message: DENIED_MESSAGE },
            counted: 'refused',
            stop: false,
          };
        }
        return {
          ack: {
            ...key,
            outcome: 'retry',
            code: 'denied',
            message: DENIED_MESSAGE,
            retry_at: retryAt(this.clock(), job.attempts),
          },
          counted: 'retried',
          stop: false,
        };
      default:
        // offline or a server failure: every other file would fail the same way
        return {
          ack: {
            ...key,
            outcome: 'retry',
            code: e.code,
            message: e.message,
            retry_at: retryAt(this.clock(), job.attempts),
          },
          counted: 'retried',
          stop: true,
        };
    }
  }
}
